export interface ItemsRequiredRow {
    items?: any;
    quantity?: number | null;
}

export const ITEMS_REQUIRED_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/items-required';

const itemsUnitSuffixRe = /\(\s*in\s*([^)]+?)\s*\)\s*$/i;

function extractUnitFromDisplay(display?: string | null): string | undefined {
    if (!display) return undefined;
    return display.match(itemsUnitSuffixRe)?.[1]?.trim() || undefined;
}

function stripUnitSuffixFromDisplay(display?: string | null): string | undefined {
    if (!display) return undefined;
    return display.replace(itemsUnitSuffixRe, '').trim();
}

function getConceptMatchKey(concept: any): string {
    const coding = concept?.coding?.[0] ?? concept;
    const system = (coding?.system ?? '').toString().trim().toLowerCase();
    const code = (coding?.code ?? '').toString().trim().toLowerCase();
    if (system && code) return `${system}::${code}`;

    const text = (concept?.text ?? concept?.display ?? concept?.coding?.[0]?.display ?? '').toString().trim().toLowerCase();
    return text;
}

function getExistingUnitForItem(activityDefinition: any, itemConcept: any): string | undefined {
    if (!activityDefinition) return undefined;
    const targetKey = getConceptMatchKey(itemConcept);
    if (!targetKey) return undefined;

    const exts = Array.isArray(activityDefinition?.extension) ? activityDefinition.extension : [];
    const itemReqExts = exts.filter((e: any) => e?.url === ITEMS_REQUIRED_EXTENSION_URL);

    for (const ext of itemReqExts) {
        const sub = Array.isArray(ext?.extension) ? ext.extension : [];
        const itemExt = sub.find((e: any) => e?.url === 'item');
        const quantityExt = sub.find((e: any) => e?.url === 'quantity');
        const existingItem = itemExt?.valueCodeableConcept;
        const existingKey = getConceptMatchKey(existingItem);

        if (existingKey && existingKey === targetKey) {
            const unit = quantityExt?.valueQuantity?.unit;
            return unit ? unit.toString() : undefined;
        }
    }

    return undefined;
}

export function extractItemsRequiredRows(activityDefinition: any | null): ItemsRequiredRow[] {
    if (!activityDefinition) return [];
    const exts = Array.isArray(activityDefinition?.extension) ? activityDefinition.extension : [];
    const itemExts = exts.filter((e: any) => e?.url === ITEMS_REQUIRED_EXTENSION_URL);

    return itemExts
        .map((ext: any) => {
            const sub = Array.isArray(ext?.extension) ? ext.extension : [];
            const itemExt = sub.find((e: any) => e?.url === 'item');
            const quantityExt = sub.find((e: any) => e?.url === 'quantity');
            return {
                items: itemExt?.valueCodeableConcept,
                quantity: quantityExt?.valueQuantity?.value ?? null,
            } as ItemsRequiredRow;
        })
        .filter((row: any) => !!row?.items || row?.quantity !== null);
}

export function applyItemsRequiredRowsToActivityDefinition(
    baseActivityDefinition: any,
    rows: ItemsRequiredRow[],
    opts?: { preserveUnitsFrom?: any; backendSystemFallback?: string }
): any {
    const next = structuredClone(baseActivityDefinition ?? {});

    const preserveFrom = opts?.preserveUnitsFrom ?? baseActivityDefinition;
    const fallbackSystem = (opts?.backendSystemFallback ?? '').toString();

    const existingExts = Array.isArray(next?.extension) ? next.extension : [];
    const keptExts = existingExts.filter((e: any) => e?.url !== ITEMS_REQUIRED_EXTENSION_URL);

    const newExts = (rows || [])
        .map((row: any) => {
            const itemConcept = row?.items;
            const quantityRaw = row?.quantity;
            const quantityValue = quantityRaw === '' || quantityRaw === null || quantityRaw === undefined ? undefined : Number(quantityRaw);

            if (!itemConcept && (quantityValue === undefined || Number.isNaN(quantityValue))) return null;

            const ext: any = { url: ITEMS_REQUIRED_EXTENSION_URL, extension: [] };

            if (itemConcept) {
                const coding0 = itemConcept?.coding?.[0] ?? itemConcept;
                const cleanedDisplay = stripUnitSuffixFromDisplay(coding0?.display) ?? coding0?.display;

                ext.extension.push({
                    url: 'item',
                    valueCodeableConcept: {
                        ...(coding0?.code ? {
                            coding: [{
                                code: coding0.code,
                                display: cleanedDisplay,
                                system: coding0.system || fallbackSystem || undefined,
                            }]
                        } : {}),
                        ...(itemConcept?.text ? { text: itemConcept.text } : {}),
                        ...(!coding0?.code && itemConcept?.coding ? { coding: itemConcept.coding } : {})
                    }
                });
            }

            if (quantityValue !== undefined && !Number.isNaN(quantityValue)) {
                const coding0 = itemConcept?.coding?.[0] ?? itemConcept;
                const unitFromDisplay = extractUnitFromDisplay(coding0?.display);
                ext.extension.push({
                    url: 'quantity',
                    valueQuantity: {
                        value: quantityValue,
                        ...(itemConcept ? {
                            unit: unitFromDisplay ?? getExistingUnitForItem(preserveFrom, itemConcept) ?? ''
                        } : {})
                    }
                });
            }

            return ext;
        })
        .filter(Boolean);

    next.extension = [...keptExts, ...newExts];
    return next;
}
